import type { EditSeason } from '@/lib/season-edit/picks';

// ── Types ────────────────────────────────────────────────────────────────

export type GuideSeason = EditSeason | 'practical';

const ALLOWED_SEASONS: readonly GuideSeason[] = [
  'winter',
  'summer',
  'festival',
  'practical',
];

export type DestinationGuide = {
  slug: string;
  title: string;
  region: string;
  tagline: string;
  desc: string;
  emoji: string;
  season: GuideSeason;
};

export type JobGuide = {
  slug: string;
  title: string;
  desc: string;
  category: string;
  emoji: string;
  season: GuideSeason;
};

export type PracticalGuide = {
  slug: string;
  title: string;
  desc: string;
  emoji: string;
  season: GuideSeason;
};

export type RegionGroup = {
  id: string;
  name: string;
  description: string;
  gradient: string;
  accentColor: string;
  guides: DestinationGuide[];
};

// ── Validation ───────────────────────────────────────────────────────────

type Bundle = {
  destinations: DestinationGuide[];
  jobs: JobGuide[];
  practicals: PracticalGuide[];
};

export function validateGuides(b: Bundle): Bundle {
  const errors: string[] = [];
  const seenSlugs = new Set<string>();

  const check = (
    kind: string,
    list: Array<DestinationGuide | JobGuide | PracticalGuide>,
  ) => {
    for (const [i, g] of list.entries()) {
      const ctx = `${kind}[${i}] (slug="${g.slug || '<missing>'}")`;
      if (!g.slug) errors.push(`${ctx}: slug is required`);
      if (seenSlugs.has(g.slug)) errors.push(`${ctx}: duplicate slug "${g.slug}"`);
      seenSlugs.add(g.slug);
      if (!g.season) errors.push(`${ctx}: season is required`);
      else if (!ALLOWED_SEASONS.includes(g.season)) {
        errors.push(
          `${ctx}: season "${g.season}" must be one of ${ALLOWED_SEASONS.join('|')}`,
        );
      }
    }
  };

  check('destinations', b.destinations);
  check('jobs', b.jobs);
  check('practicals', b.practicals);

  if (errors.length > 0) {
    const msg = `Invalid guides:\n  - ${errors.join('\n  - ')}`;
    if (process.env.NODE_ENV !== 'production') throw new Error(msg);
    console.error('[guides]', msg);
  }

  return b;
}

// ── Data ─────────────────────────────────────────────────────────────────
// Populated in Task 5. REGIONS arrays contain DestinationGuide lists;
// keep the REGIONS shape + the flat arrays in sync.

export const REGIONS: RegionGroup[] = [
  {
    id: "alps",
    name: "European Alps",
    description:
      "Winter ski season from December to April. Chalets, ski schools, mountain hospitality across France, Switzerland and Austria.",
    gradient: "linear-gradient(135deg, #1B2840 0%, #2A3F6A 60%, #7A8B6F 100%)",
    accentColor: "#2A3F6A",
    guides: [
      {
        slug: "meribel",
        title: "Méribel",
        region: "France · Three Valleys",
        tagline: "The seasonaire classic",
        desc: "Three Valleys ski jobs, pay, accommodation and resort life.",
        emoji: "🏔️",
        season: "winter",
      },
      {
        slug: "val-disere",
        title: "Val d'Isère",
        region: "France · Espace Killy",
        tagline: "High altitude, high energy",
        desc: "Espace Killy ski jobs, accommodation and nightlife.",
        emoji: "❄️",
        season: "winter",
      },
      {
        slug: "tignes",
        title: "Tignes",
        region: "France · Espace Killy",
        tagline: "Linked with Val d'Isère",
        desc: "Glacier skiing, purpose-built resort, cheaper than its neighbour.",
        emoji: "⛷️",
        season: "winter",
      },
      {
        slug: "courchevel",
        title: "Courchevel",
        region: "France · Three Valleys",
        tagline: "Three Valleys luxury",
        desc: "Luxury chalet work, Michelin kitchens, five-star staff.",
        emoji: "✨",
        season: "winter",
      },
      {
        slug: "verbier",
        title: "Verbier",
        region: "Switzerland · 4 Vallées",
        tagline: "Steep, stylish, Swiss",
        desc: "Swiss Alps season work, salaries and living costs.",
        emoji: "🗻",
        season: "winter",
      },
      {
        slug: "zermatt",
        title: "Zermatt",
        region: "Switzerland · Matterhorn",
        tagline: "The Matterhorn resort",
        desc: "Car-free Swiss resort with year-round glacier skiing.",
        emoji: "⛰️",
        season: "winter",
      },
      {
        slug: "chamonix",
        title: "Chamonix",
        region: "France · Mont Blanc",
        tagline: "The birthplace of alpinism",
        desc: "Mont Blanc Valley seasonal roles and resort life.",
        emoji: "🧗",
        season: "winter",
      },
      {
        slug: "morzine",
        title: "Morzine",
        region: "France · Portes du Soleil",
        tagline: "Tight-knit British scene",
        desc: "Portes du Soleil season jobs, pay and tips.",
        emoji: "🇫🇷",
        season: "winter",
      },
      {
        slug: "les-gets",
        title: "Les Gets",
        region: "France · Portes du Soleil",
        tagline: "Morzine's quieter neighbour",
        desc: "Family-friendly Portes du Soleil village, chalet heavy.",
        emoji: "🌲",
        season: "winter",
      },
      {
        slug: "st-anton",
        title: "St Anton",
        region: "Austria · Arlberg",
        tagline: "The party resort",
        desc: "Austrian Arlberg, legendary après-ski and chalet work.",
        emoji: "🍻",
        season: "winter",
      },
    ],
  },
  {
    id: "japan",
    name: "Japan",
    description:
      "Japan's ski season runs December to March. Famous for waist-deep powder, onsen culture and growing seasonaire demand.",
    gradient:
      "linear-gradient(135deg, #1B2840 0%, #3A4B7A 60%, #C8D4E6 100%)",
    accentColor: "#3A4B7A",
    guides: [
      {
        slug: "niseko",
        title: "Niseko",
        region: "Japan · Hokkaido",
        tagline: "Japow capital",
        desc: "Hokkaido powder, English-speaking hires, onsen life.",
        emoji: "🗾",
        season: "winter",
      },
    ],
  },
  {
    id: "north-america",
    name: "North America",
    description:
      "Canada and the US for winter work. Working holiday visas available for Brits aged 18–35. Long seasons, big mountains.",
    gradient:
      "linear-gradient(135deg, #1B2840 0%, #355F7A 60%, #6B9AB0 100%)",
    accentColor: "#355F7A",
    guides: [
      {
        slug: "whistler",
        title: "Whistler",
        region: "Canada · British Columbia",
        tagline: "The biggest ski resort in North America",
        desc: "Canadian ski season work, visas and resort guide.",
        emoji: "🇨🇦",
        season: "winter",
      },
    ],
  },
  {
    id: "mediterranean",
    name: "Mediterranean & Islands",
    description:
      "Summer season from May to October. Hospitality, beach clubs, watersports and yachting across the Med and Atlantic islands.",
    gradient:
      "linear-gradient(135deg, #1B2840 0%, #2D6B7F 60%, #D9A57A 100%)",
    accentColor: "#2D6B7F",
    guides: [
      {
        slug: "ibiza",
        title: "Ibiza",
        region: "Spain · Balearics",
        tagline: "The nightlife island",
        desc: "Summer season jobs, nightlife work and island life.",
        emoji: "🌴",
        season: "summer",
      },
      {
        slug: "mallorca",
        title: "Mallorca",
        region: "Spain · Balearics",
        tagline: "More than just Palma",
        desc: "Island-wide hospitality, tourism and watersports work.",
        emoji: "🏖️",
        season: "summer",
      },
      {
        slug: "mykonos",
        title: "Mykonos",
        region: "Greece · Cyclades",
        tagline: "Luxury Greek islands",
        desc: "Greek island summer jobs, hospitality and yachting.",
        emoji: "🇬🇷",
        season: "summer",
      },
      {
        slug: "corfu",
        title: "Corfu",
        region: "Greece · Ionian Islands",
        tagline: "The British favourite",
        desc: "Ionian island hospitality, watersports and villa work.",
        emoji: "🫒",
        season: "summer",
      },
      {
        slug: "croatia",
        title: "Croatia",
        region: "Croatia · Dalmatian Coast",
        tagline: "Adriatic bluewater",
        desc: "Split, Hvar and Dubrovnik summer work.",
        emoji: "⛵",
        season: "summer",
      },
      {
        slug: "montenegro",
        title: "Montenegro",
        region: "Montenegro · Bay of Kotor",
        tagline: "The Adriatic's rising star",
        desc: "Emerging Bay of Kotor yacht and hospitality scene.",
        emoji: "🏔️",
        season: "summer",
      },
      {
        slug: "sardinia",
        title: "Sardinia",
        region: "Italy · Costa Smeralda",
        tagline: "Italian luxury coastline",
        desc: "Costa Smeralda yacht and hospitality season.",
        emoji: "🇮🇹",
        season: "summer",
      },
      {
        slug: "lake-garda",
        title: "Lake Garda",
        region: "Italy · Northern Lakes",
        tagline: "Alps meet the Med",
        desc: "Italian lake hospitality, watersports, biking tourism.",
        emoji: "🚤",
        season: "summer",
      },
      {
        slug: "tenerife",
        title: "Tenerife",
        region: "Spain · Canary Islands",
        tagline: "Year-round season",
        desc: "Canary Islands hospitality and watersports, all year.",
        emoji: "🌋",
        season: "summer",
      },
      {
        slug: "greece-summer-jobs",
        title: "Summer Jobs in Greece",
        region: "Greece · All Islands",
        tagline: "Overview of Greek summer work",
        desc: "Island hopping, hospitality roles and how to get hired.",
        emoji: "☀️",
        season: "summer",
      },
      {
        slug: "portugal-summer-jobs",
        title: "Summer Jobs in Portugal",
        region: "Portugal · Algarve & Lisbon",
        tagline: "Algarve to Lisbon coast",
        desc: "Seasonal work across Portugal from the Algarve to Lisbon.",
        emoji: "🏄",
        season: "summer",
      },
    ],
  },
  {
    id: "yacht-hubs",
    name: "Yacht Hubs",
    description:
      "The ports where yacht crew are hired. Dockwalk, day work, and the boat-by-boat reality of getting your first yacht job.",
    gradient:
      "linear-gradient(135deg, #1B2840 0%, #2A5A7A 60%, #E8D5B0 100%)",
    accentColor: "#2A5A7A",
    guides: [
      {
        slug: "antibes",
        title: "Antibes",
        region: "France · Côte d'Azur",
        tagline: "The Med's yacht hub",
        desc: "French Riviera yacht jobs, dockwalking and crew life.",
        emoji: "⚓",
        season: "summer",
      },
      {
        slug: "palma",
        title: "Palma",
        region: "Spain · Mallorca",
        tagline: "Spanish superyacht capital",
        desc: "Mallorca yacht crew work and superyacht hub guide.",
        emoji: "⛵",
        season: "summer",
      },
      {
        slug: "fort-lauderdale",
        title: "Fort Lauderdale",
        region: "USA · Florida",
        tagline: "The US yachting capital",
        desc: "US yacht crew jobs, visas and the Fort Lauderdale scene.",
        emoji: "🇺🇸",
        season: "summer",
      },
      {
        slug: "caribbean",
        title: "Caribbean",
        region: "Caribbean · Winter Season",
        tagline: "Winter charter season",
        desc: "Yacht season jobs, crew work and island hopping.",
        emoji: "🏝️",
        season: "summer",
      },
    ],
  },
];

export const JOB_GUIDES: JobGuide[] = [
  {
    slug: "last-minute-ski-season-jobs",
    title: "Last-Minute Ski Season Jobs 2026/27",
    desc: "Is it too late for a ski season? Who still hires from October to January, French job fairs and UK work rights.",
    category: "Ski",
    emoji: "⏱️",
    season: "winter",
  },
  {
    slug: "chalet-host-jobs",
    title: "Chalet Host Jobs",
    desc: "The classic ski season role. What the job actually involves, pay, and how to land one.",
    category: "Ski",
    emoji: "🏠",
    season: "winter",
  },
  {
    slug: "ski-instructor-jobs",
    title: "Ski Instructor Jobs",
    desc: "BASI levels, gap year options and where qualified instructors earn the most.",
    category: "Ski",
    emoji: "⛷️",
    season: "winter",
  },
  {
    slug: "transfer-driver-jobs",
    title: "Transfer Driver Jobs",
    desc: "Ski resort airport transfers. Licences, D1 entitlement, and what a Saturday looks like.",
    category: "Ski",
    emoji: "🚐",
    season: "winter",
  },
  {
    slug: "kitchen-chef-jobs-seasonal",
    title: "Kitchen & Chef Jobs",
    desc: "From KP to head chef. Qualifications, pay bands, and the reality of seasonal kitchens.",
    category: "Hospitality",
    emoji: "👨‍🍳",
    season: "practical",
  },
  {
    slug: "bar-nightlife-jobs-abroad",
    title: "Bar & Nightlife Jobs Abroad",
    desc: "Ski and summer bar work, from après-ski to Ibiza superclubs.",
    category: "Hospitality",
    emoji: "🍸",
    season: "practical",
  },
  {
    slug: "yacht-crew-jobs-no-experience",
    title: "Yacht Crew Jobs (No Experience)",
    desc: "How to get into yachting with no experience. STCW, dockwalking and entry roles.",
    category: "Yacht",
    emoji: "⚓",
    season: "summer",
  },
  {
    slug: "yacht-stewardess-jobs",
    title: "Yacht Stewardess Jobs",
    desc: "The most common entry route for women into yachting. Training, duties, pay.",
    category: "Yacht",
    emoji: "🛥️",
    season: "summer",
  },
  {
    slug: "water-sports-instructor-jobs",
    title: "Water Sports Instructor Jobs",
    desc: "Sailing, windsurfing, kitesurfing, wakeboard, paddleboard. Qualifications and where to work.",
    category: "Watersports",
    emoji: "🌊",
    season: "summer",
  },
  {
    slug: "surf-instructor-jobs",
    title: "Surf Instructor Jobs",
    desc: "Qualifications, pay and where to find surf teaching work.",
    category: "Watersports",
    emoji: "🏄",
    season: "summer",
  },
  {
    slug: "summer-camp-jobs-europe",
    title: "Summer Camp Jobs Europe",
    desc: "Activity camps, language camps, Camp America alternatives closer to home.",
    category: "Summer",
    emoji: "🏕️",
    season: "summer",
  },
  {
    slug: "festival-jobs-uk",
    title: "UK Festival Jobs",
    desc: "How to get hired for festivals across the UK, every summer.",
    category: "Festival",
    emoji: "🎪",
    season: "festival",
  },
  {
    slug: "festival-bar-staff-jobs-uk",
    title: "Festival Bar Staff Jobs UK",
    desc: "The most-hired festival role. Agencies, pay, volunteer schemes and what to expect.",
    category: "Festival",
    emoji: "🍺",
    season: "festival",
  },
  {
    slug: "festival-catering-jobs",
    title: "Festival Catering Jobs",
    desc: "Getting hired by food traders and catering companies.",
    category: "Festival",
    emoji: "🌯",
    season: "festival",
  },
  {
    slug: "glastonbury-jobs",
    title: "Glastonbury Jobs",
    desc: "Working at Glastonbury: roles, pay and what to expect.",
    category: "Festival",
    emoji: "🎤",
    season: "festival",
  },
  {
    slug: "boomtown-jobs",
    title: "Boomtown Fair Jobs",
    desc: "Roles, pay and tips for working at Boomtown.",
    category: "Festival",
    emoji: "🎭",
    season: "festival",
  },
];

export const PRACTICAL_GUIDES: PracticalGuide[] = [
  {
    slug: "seasonal-hiring-calendar",
    title: "Seasonal Hiring Calendar 2026/27",
    desc: "When ski, summer, yacht and festival employers hire, month by month, with dated job fairs.",
    emoji: "🗓️",
    season: "practical",
  },
  {
    slug: "seasonal-job-platforms",
    title: "Where to Find Seasonal Work",
    desc: "Every job board, platform and agency for seasonal work, compared honestly.",
    emoji: "🧭",
    season: "practical",
  },
  {
    slug: "festival-job-platforms",
    title: "Festival Job Platforms",
    desc: "Volunteer schemes, staffing agencies and boards for UK festival work.",
    emoji: "🎪",
    season: "practical",
  },
  {
    slug: "summer-job-platforms",
    title: "Summer Job Platforms",
    desc: "Yacht, watersports and Mediterranean hospitality platforms compared.",
    emoji: "⛵",
    season: "practical",
  },
  {
    slug: "seasonal-work-facebook-groups",
    title: "Seasonal Work Facebook Groups",
    desc: "A curated directory of ski, yacht and festival job groups.",
    emoji: "💬",
    season: "practical",
  },
  {
    slug: "season-job-no-experience",
    title: "Season Job with No Experience",
    desc: "How to land your first seasonal role without a track record.",
    emoji: "🎯",
    season: "practical",
  },
  {
    slug: "how-to-write-a-seasonal-cv",
    title: "Writing a Seasonal CV",
    desc: "What employers actually look for in a season worker CV.",
    emoji: "📄",
    season: "practical",
  },
  {
    slug: "seasonal-work-visa-guide",
    title: "Seasonal Work Visas",
    desc: "Right to work, visa options and post-Brexit rules explained.",
    emoji: "🛂",
    season: "practical",
  },
  {
    slug: "tax-national-insurance-seasonal-workers",
    title: "Tax & National Insurance",
    desc: "How seasonal work affects your UK tax, NI and student loan.",
    emoji: "💷",
    season: "practical",
  },
  {
    slug: "accommodation-guide-seasonal-workers",
    title: "Accommodation Guide",
    desc: "Staff accommodation, renting abroad and what to look for.",
    emoji: "🛏️",
    season: "practical",
  },
  {
    slug: "how-to-save-money-on-a-season",
    title: "How to Save on a Season",
    desc: "Realistic savings, how to cut costs, and the mistakes that drain your bank.",
    emoji: "💰",
    season: "practical",
  },
  {
    slug: "travel-insurance-seasonal-workers",
    title: "Travel Insurance",
    desc: "Why standard policies fail seasonaires and what to buy instead.",
    emoji: "🩹",
    season: "practical",
  },
  {
    slug: "stcw-certification-guide",
    title: "STCW Certification Guide",
    desc: "The yachting prerequisite. What it covers, where to do it, how much it costs.",
    emoji: "🛟",
    season: "practical",
  },
  {
    slug: "dbs-food-hygiene-seasonal-work",
    title: "DBS Checks & Food Hygiene",
    desc: "The two certificates employers ask for most. How to get them quickly.",
    emoji: "✅",
    season: "practical",
  },
  {
    slug: "packing-for-a-season",
    title: "Packing for a Season",
    desc: "The complete packing list for ski, summer and festival seasons.",
    emoji: "🎒",
    season: "practical",
  },
  {
    slug: "what-to-wear-skiing",
    title: "What to Wear Skiing",
    desc: "A layering guide for every temperature on the mountain.",
    emoji: "🧥",
    season: "winter",
  },
  {
    slug: "uk-festivals",
    title: "UK Festival Jobs",
    desc: "Overview of seasonal festival work across the UK.",
    emoji: "🎶",
    season: "festival",
  },
];

// ── Conditions guides ──────────────────────────────────────────────────────
// Educational "how to read the conditions" explainers, one per major sport.
// They are standalone teaching articles, not descriptions of PeakWave's
// scoring. Kept season: "practical" so exploreGuidePath routes them to
// /guides/<slug>. Rendered in their own section on the /guides index.
export const CONDITIONS_GUIDES: PracticalGuide[] = [
  {
    slug: "how-to-read-surf-conditions",
    title: "How to Read Surf Conditions",
    desc: "Swell height, period, direction, wind and tides explained. What makes a good surf day.",
    emoji: "🏄",
    season: "practical",
  },
  {
    slug: "how-to-read-snow-conditions",
    title: "How to Read Snow Conditions",
    desc: "Fresh snow, base depth, wind, visibility and grooming decoded before you head up.",
    emoji: "🏔️",
    season: "practical",
  },
  {
    slug: "how-to-read-wind-conditions",
    title: "How to Read Wind Conditions",
    desc: "Wind speed, direction, gusts and consistency for wing foil, kite and windsurf.",
    emoji: "💨",
    season: "practical",
  },
  {
    slug: "how-to-read-diving-conditions",
    title: "How to Read Diving Conditions",
    desc: "Visibility, tides, current, swell and water temperature explained for divers.",
    emoji: "🤿",
    season: "practical",
  },
  {
    slug: "how-to-read-mtb-trail-conditions",
    title: "How to Read MTB Trail Conditions",
    desc: "Soil moisture, rain history, freeze-thaw and when trails are too wet to ride.",
    emoji: "🚵",
    season: "practical",
  },
  {
    slug: "how-to-read-sup-conditions",
    title: "How to Read SUP Conditions",
    desc: "Wind, direction, tides and water state for stand-up paddleboarding, and how to pick your window.",
    emoji: "🛶",
    season: "practical",
  },
];

validateGuides({
  destinations: REGIONS.flatMap((r) => r.guides),
  jobs: JOB_GUIDES,
  // Conditions guides share the PracticalGuide shape; validate them alongside
  // the practical guides so slug collisions across every guide are caught.
  practicals: [...PRACTICAL_GUIDES, ...CONDITIONS_GUIDES],
});
